import axios from 'axios';
import {
  API_BASE_DIAGNOSTIC_IMAGING_URL,
  API_DIAGNOSTIC_IMAGING_LIST,
  API_DIAGNOSTIC_IMAGING_DETAILS,
  API_DIAGNOSTIC_IMAGING_CREATE,
  API_DIAGNOSTIC_IMAGING_UPDATE,
  API_DIAGNOSTIC_IMAGING_DELETE,
  API_DIAGNOSTIC_IMAGING_STATS,
  API_DIAGNOSTIC_IMAGING_STATUS,
  API_DIAGNOSTIC_IMAGING_BY_PATIENT,
  API_DIAGNOSTIC_IMAGING_REMOVE_FILE
} from '../../../helpers/urlHelper';

// ========================================================================
// 🏥 SERVICIO DE IMÁGENES DIAGNÓSTICAS MEJORADO
// ========================================================================

class DiagnosticImagingService {
  
  // ========================================================================
  // 📋 FUNCIONES HELPER PARA CONSTRUCCIÓN DE URLs
  // ========================================================================

  /**
   * Construye URL para obtener un estudio específico
   * @param {string} diagnosticId - ID del estudio
   * @returns {string} URL completa
   */
  getDiagnosticDetailsUrl(diagnosticId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_DETAILS}/${diagnosticId}`;
  }

  /**
   * Construye URL para actualizar un estudio específico
   * @param {string} diagnosticId - ID del estudio
   * @returns {string} URL completa
   */
  getDiagnosticUpdateUrl(diagnosticId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_UPDATE}/${diagnosticId}`;
  }

  /**
   * Construye URL para eliminar un estudio específico
   * @param {string} diagnosticId - ID del estudio
   * @returns {string} URL completa
   */
  getDiagnosticDeleteUrl(diagnosticId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_DELETE}/${diagnosticId}`;
  }

  /**
   * Construye URL para cambiar el estado de un estudio
   * @param {string} diagnosticId - ID del estudio
   * @returns {string} URL completa
   */
  getDiagnosticStatusUrl(diagnosticId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_STATUS}/${diagnosticId}/status`;
  }

  /**
   * Construye URL para obtener estudios de un paciente específico
   * @param {string} patientId - ID del paciente
   * @returns {string} URL completa
   */
  getDiagnosticsByPatientUrl(patientId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_BY_PATIENT}/${patientId}`;
  }

  /**
   * Construye URL para eliminar un archivo específico de un estudio
   * @param {string} diagnosticId - ID del estudio
   * @param {string} fileId - ID del archivo
   * @returns {string} URL completa
   */
  getDiagnosticRemoveFileUrl(diagnosticId, fileId) {
    return `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_REMOVE_FILE}/${diagnosticId}/files/${fileId}`;
  }

  // ========================================================================
  // 🔧 MÉTODOS DE UTILIDAD
  // ========================================================================

  // Helper method to handle API errors
  handleApiError(error, operation) {
    console.error(`Error en ${operation}:`, error);
    
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`HTTP ${status}:`, data);
      
      return {
        success: false,
        message: data?.message || data?.error || `HTTP ${status} Error`,
        status,
        details: data
      };
    } else if (error.request) {
      // Request was made but no response
      console.error('No response received:', error.request);
      return {
        success: false,
        message: 'No response from server. Please check your connection.',
        status: 0
      };
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
      return {
        success: false,
        message: error.message || 'Unknown error occurred',
        status: -1
      };
    }
  }

  // ========================================================================
  // 📋 MÉTODOS PRINCIPALES DEL SERVICIO
  // ========================================================================

  // 📋 Obtener todas las imágenes diagnósticas
  async getAllDiagnosticImages() {
    try {
      const response = await axios.get(
        `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_LIST}`
      );
      
      return {
        success: true,
        data: response.data?.data || response.data || [],
        message: 'Images loaded successfully'
      };
    } catch (error) {
      return this.handleApiError(error, 'getAllDiagnosticImages');
    }
  }

  // 📊 Obtener estadísticas
  async getDiagnosticStats() {
    try {
      const response = await axios.get(
        `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_STATS}`
      );
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Stats loaded successfully'
      };
    } catch (error) {
      return this.handleApiError(error, 'getDiagnosticStats');
    }
  }

  // 🔍 Obtener imagen diagnóstica por ID
  async getDiagnosticImageById(id) {
    try {
      if (!id) {
        throw new Error('ID is required');
      }

      const response = await axios.get(this.getDiagnosticDetailsUrl(id));
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Image loaded successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `getDiagnosticImageById(${id})`);
    }
  }

  // 🏥 Obtener estudios por paciente
  async getDiagnosticsByPatient(patientId) {
    try {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }

      const response = await axios.get(this.getDiagnosticsByPatientUrl(patientId));
      
      return {
        success: true,
        data: response.data?.data || response.data || [],
        message: 'Patient diagnostics loaded successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `getDiagnosticsByPatient(${patientId})`);
    }
  }

  // ➕ Crear nueva imagen diagnóstica (con archivos)
  async createDiagnosticImage(diagnosticData, files = []) {
  try {
    // Validate required fields
    const requiredFields = ['studyName', 'studyType', 'patientId'];
    const missingFields = requiredFields.filter(field => !diagnosticData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Prepare form data
    const formData = new FormData();
    
    // Add diagnostic data with proper field mapping
    const fieldMapping = {
      studyName: 'studyName',
      studyType: 'studyType',
      bodyPart: 'bodyPart',
      status: 'status',
      priority: 'priority',
      consent: 'consent',
      notes: 'notes',
      doctorNotes: 'doctorNotes',
      scheduledDate: 'scheduledDate',
      patientId: 'patientId',
      doctorId: 'doctorId'  // ← LÍNEA AGREGADA
    };

    Object.keys(fieldMapping).forEach(key => {
      const value = diagnosticData[key];
      if (value !== undefined && value !== null && value !== '') {
        // Handle boolean values
        if (typeof value === 'boolean') {
          formData.append(fieldMapping[key], value.toString());
        } 
        // Handle date values
        else if (key === 'scheduledDate' && value) {
          formData.append(fieldMapping[key], new Date(value).toISOString());
        }
        // Handle other values
        else {
          formData.append(fieldMapping[key], value);
        }
      }
    });

    // Add files if they exist (maximum 5)
    if (files && files.length > 0) {
      files.slice(0, 5).forEach((file, index) => {
        formData.append('files', file);
      });
    }

    // Log the form data for debugging
    console.log('Sending diagnostic data:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    const response = await axios.post(
      `${API_BASE_DIAGNOSTIC_IMAGING_URL}${API_DIAGNOSTIC_IMAGING_CREATE}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 seconds timeout
      }
    );

    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || 'Diagnostic study created successfully'
    };
  } catch (error) {
    return this.handleApiError(error, 'createDiagnosticImage');
  }
}

  // ✏️ Actualizar imagen diagnóstica (con archivos opcionales)
  async updateDiagnosticImage(id, diagnosticData, files = []) {
    try {
      if (!id) {
        throw new Error('ID is required for update');
      }

      const formData = new FormData();
      
      // Add updated data with proper validation
      Object.keys(diagnosticData).forEach(key => {
        const value = diagnosticData[key];
        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'boolean') {
            formData.append(key, value.toString());
          } else if (key === 'scheduledDate' && value) {
            formData.append(key, new Date(value).toISOString());
          } else {
            formData.append(key, value);
          }
        }
      });
      
      // Add new files if they exist
      if (files && files.length > 0) {
        files.slice(0, 5).forEach((file) => {
          formData.append('files', file);
        });
      }

      const response = await axios.put(
        this.getDiagnosticUpdateUrl(id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Diagnostic study updated successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `updateDiagnosticImage(${id})`);
    }
  }

  // 🔄 Actualizar solo el estado
  async updateDiagnosticStatus(id, status) {
    try {
      if (!id) {
        throw new Error('ID is required');
      }
      
      if (!status) {
        throw new Error('Status is required');
      }

      const validStatuses = ['pending', 'in_progress', 'completed', 'cancelled'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
      }

      const response = await axios.patch(
        this.getDiagnosticStatusUrl(id),
        { status },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Status updated successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `updateDiagnosticStatus(${id})`);
    }
  }

  // 🗑️ Eliminar imagen diagnóstica
  async deleteDiagnosticImage(id) {
    try {
      if (!id) {
        throw new Error('ID is required');
      }

      const response = await axios.delete(this.getDiagnosticDeleteUrl(id));

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Diagnostic study deleted successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `deleteDiagnosticImage(${id})`);
    }
  }

  // 🗑️ Eliminar archivo específico de un estudio
  async removeDiagnosticFile(diagnosticId, fileId) {
    try {
      if (!diagnosticId) {
        throw new Error('Diagnostic ID is required');
      }
      
      if (!fileId) {
        throw new Error('File ID is required');
      }

      const response = await axios.delete(this.getDiagnosticRemoveFileUrl(diagnosticId, fileId));

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'File removed successfully'
      };
    } catch (error) {
      return this.handleApiError(error, `removeDiagnosticFile(${diagnosticId}, ${fileId})`);
    }
  }

  // 🔧 Test API connection
  async testConnection() {
    try {
      const response = await axios.get(
        `${API_BASE_DIAGNOSTIC_IMAGING_URL}/health`,
        { timeout: 5000 }
      );
      
      return {
        success: true,
        message: 'API connection successful',
        data: response.data
      };
    } catch (error) {
      return this.handleApiError(error, 'testConnection');
    }
  }
}

// Instancia única del servicio
const diagnosticImagingService = new DiagnosticImagingService();

export default diagnosticImagingService;